import { useEffect, useState, type FormEvent } from 'react'
import type { Account } from './types'
import type { CategoryGroup } from '@/features/budget/types'
import { useCreateTransaction } from './hooks'
import { CategoryDropdown } from './CategoryDropdown'

interface Props {
  open: boolean
  onClose: () => void
  accounts: Account[]
  groups: CategoryGroup[]
  /** Default account selection — last-used or first available. */
  defaultAccountId?: string
}

/**
 * Manual entry for transactions that don't come from Plaid. Common
 * cases: cash purchases, splitting a Venmo transaction, recording
 * a check, fixing what Plaid mis-imported.
 *
 * The amount field defaults to outflow (negative) since that's the
 * common case; tick "Inflow" to flip.
 *
 * Today (2026-06-07) is the default date — local-time, so the user
 * sees what they expect. Stored as an ISO-8601 UTC string to match
 * iOS persistence.
 */
export function AddTransactionDialog({ open, onClose, accounts, groups, defaultAccountId }: Props) {
  const create = useCreateTransaction()
  const [date, setDate] = useState(() => isoToInputDate(new Date().toISOString()))
  const [accountId, setAccountId] = useState(defaultAccountId ?? '')
  const [amount, setAmount] = useState('')
  const [isInflow, setIsInflow] = useState(false)
  const [categoryId, setCategoryId] = useState<string | null>(null)
  const [memo, setMemo] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setDate(isoToInputDate(new Date().toISOString()))
      setAccountId(defaultAccountId ?? accounts[0]?.id ?? '')
      setAmount('')
      setIsInflow(false)
      setCategoryId(null)
      setMemo('')
      setError(null)
    }
  }, [open, defaultAccountId, accounts])

  if (!open) return null

  function submit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    const cleaned = amount.replace(/[$,]/g, '').trim()
    const parsed = Number(cleaned)
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setError('Enter a positive amount')
      return
    }
    if (!accountId) {
      setError('Pick an account')
      return
    }
    const signed = isInflow ? Math.abs(parsed) : -Math.abs(parsed)
    create.mutate(
      {
        dateISO: inputDateToISO(date),
        accountId,
        amount: signed,
        categoryId,
        memo: memo.trim() ? memo.trim() : null,
      },
      {
        onSuccess: () => onClose(),
        onError: (err) => setError(err instanceof Error ? err.message : 'Save failed'),
      },
    )
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-tx-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose()
      }}
    >
      <form
        onSubmit={submit}
        className="w-full max-w-md space-y-3 rounded-2xl border border-zinc-200 bg-white p-6 shadow-xl dark:border-zinc-800 dark:bg-zinc-900"
      >
        <h2 id="add-tx-title" className="text-lg font-semibold">
          Add transaction
        </h2>

        <label className="block text-sm">
          <span className="mb-1 block text-zinc-600 dark:text-zinc-300">Date</span>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
            className="w-full rounded-md border border-zinc-300 bg-white px-2 py-2 dark:border-zinc-700 dark:bg-zinc-950"
          />
        </label>

        <label className="block text-sm">
          <span className="mb-1 block text-zinc-600 dark:text-zinc-300">Account</span>
          <select
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            required
            className="w-full rounded-md border border-zinc-300 bg-white px-2 py-2 dark:border-zinc-700 dark:bg-zinc-950"
          >
            <option value="" disabled>
              Pick an account…
            </option>
            {accounts
              .filter((a) => !a.isHidden)
              .map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
          </select>
        </label>

        <div className="block text-sm">
          <span className="mb-1 block text-zinc-600 dark:text-zinc-300">Amount</span>
          <div className="flex items-center gap-2">
            <input
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              required
              className="flex-1 rounded-md border border-zinc-300 bg-white px-2 py-2 tabular-nums dark:border-zinc-700 dark:bg-zinc-950"
            />
            <label className="flex items-center gap-1 text-xs">
              <input
                type="checkbox"
                checked={isInflow}
                onChange={(e) => setIsInflow(e.target.checked)}
              />
              Inflow
            </label>
          </div>
        </div>

        <div className="block text-sm">
          <span className="mb-1 block text-zinc-600 dark:text-zinc-300">Category</span>
          <div className="rounded-md border border-zinc-300 dark:border-zinc-700">
            <CategoryDropdown groups={groups} value={categoryId} onChange={setCategoryId} />
          </div>
        </div>

        <label className="block text-sm">
          <span className="mb-1 block text-zinc-600 dark:text-zinc-300">Memo</span>
          <input
            type="text"
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            placeholder="optional"
            className="w-full rounded-md border border-zinc-300 bg-white px-2 py-2 dark:border-zinc-700 dark:bg-zinc-950"
          />
        </label>

        {error && (
          <p role="alert" className="text-sm text-overspent">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-zinc-300 px-4 py-2 text-sm hover:bg-zinc-100 dark:border-zinc-700 dark:hover:bg-zinc-800"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={create.isPending}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {create.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  )
}

function isoToInputDate(iso: string): string {
  // <input type="date"> expects yyyy-MM-dd in the local timezone.
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const dy = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${dy}`
}

function inputDateToISO(value: string): string {
  // Treat the date as local-noon to dodge timezone underflow that
  // could push a "Jun 7" entry to "Jun 6" UTC.
  const [y, m, d] = value.split('-').map((n) => parseInt(n, 10))
  return new Date(y, (m ?? 1) - 1, d ?? 1, 12, 0, 0).toISOString()
}
